<template>
  <section>
    <div class="w-full flex items-start justify-between">
      <h2>Assigned Seats</h2>
      <BittsButton
        text="Manage seats"
        size="small"
        variant="outline"
        @click="onManageSeats"
      />
    </div>
    <div class="w-full grid grid-cols-2 pt-8">
      <section class="p-0">
        <h4>Full Access</h4>
        <p
          data-testid="core-seat-count"
          class="font-bold text-lg text-neutral-text-strong"
        >
          {{ coreSeatCount
          }}<span class="text-neutral-text-weak opacity-50"
            >/{{ coreSeatBase }}</span
          >
        </p>
        <BittsUserStack
          v-if="coreUserStack.length"
          :users="coreUserStack"
          size="small"
        />
      </section>
      <section class="p-0 px-16 gap-8">
        <h4>Sales</h4>
        <BittsButton
          v-if="
            isFreeTier ||
            isConnectorTier /* v5 connector specifically, TODO */ ||
            !salesSeatBase
          "
          text="Talk to Sales"
          size="x-small"
          type="upsell"
          :left-icon="['fak', 'upgrade']"
          @click="
            handleBillingInteraction({
              talkToSalesReason: 'Upgrade to Supernode',
              event_site: EVENT_SITES.PLAN_SUMMARY_CARD,
            })
          "
        />
        <div v-else class="flex flex-col">
          <p
            data-testid="sales-seat-count"
            class="font-bold text-lg text-neutral-text-strong"
          >
            {{ salesSeatCount
            }}<span class="text-neutral-text-weak opacity-50"
              >/{{ salesSeatBase }}</span
            >
          </p>
          <BittsUserStack
            v-if="salesUserStack.length"
            :users="salesUserStack"
            size="small"
          />
        </div>
      </section>
    </div>
  </section>
</template>

<script setup lang="ts">
import { BittsButton, BittsUserStack } from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import useBilling from '@/composables/useBilling';
import useSeats from '@/composables/useSeats';
import { useBillingStore, useTeamStore } from '@/stores';
import { Invite, TeamAuthorization, TeamAuthorizationUser } from '@/types/team';

const { coreSeats, salesOnlyUsers } = storeToRefs(useTeamStore());

const { handleBillingInteraction } = useBilling();

const { isFreeTier, isConnectorTier } = storeToRefs(useBillingStore());
const { coreSeatCount, coreSeatBase, salesSeatCount, salesSeatBase } =
  useSeats();

type AuthOrInvite =
  | TeamAuthorization
  | (Invite & { user?: TeamAuthorizationUser });
function getUserStack(seats: AuthOrInvite[], unusedSeats: number) {
  const users = seats.map((user: AuthOrInvite) => {
    if (user.user) return user.user;
    return user;
  });

  // this is for test cases
  const ghostUsers = unusedSeats === Infinity ? 10 : unusedSeats;
  if (ghostUsers > 0) {
    for (let i = 0; i < ghostUsers; i++) {
      users.push({
        email: '',
      } as TeamAuthorizationUser);
    }
  }
  return users;
}

const salesUserStack = computed(() =>
  getUserStack(
    salesOnlyUsers.value,
    salesSeatBase.value - salesSeatCount.value,
  ),
);
const coreUserStack = computed(() =>
  getUserStack(coreSeats.value, coreSeatBase.value - coreSeatCount.value),
);

const router = useRouter();

async function onManageSeats() {
  await router.push({ name: 'team' });
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";
section {
  @apply flex flex-col gap-4 items-start;
}
</style>
